import { useEffect, useEffectEvent, useRef, useState } from 'react';

import {
  buildMobileAppEmbedUrl,
  isMobileAppSession,
  loadMobileAppSdk,
  parseMobileAppQueuePayload,
  readMobileAppErrorMessage,
} from './shared-mobile-app.helpers';
import type {
  MobileAppClient,
  MobileAppEmbedConfig,
  MobileAppQueueState,
  MobileAppSdkStatus,
  MobileAppSessionPhase,
  MobileAppSession,
} from './shared-mobile-app.types';

const TRACKED_CLIENT_EVENTS: readonly string[] = [
  'queue',
  'queueEnd',
  'sessionRequested',
  'session',
  'sessionEnded',
  'error',
  'app',
  'deviceInfo',
];

const TRACKED_SESSION_EVENTS: readonly string[] = [
  'app',
  'network',
  'log',
  'action',
  'error',
];

interface UseMobileAppClientOptions {
  iframeId: string;
  embedUrl: string;
  config: MobileAppEmbedConfig;
  enabled: boolean;
  onEvent?: (
    source: 'sdk' | 'client' | 'session',
    label: string,
    detail?: unknown
  ) => void;
  onSessionEnded?: () => void;
}

interface UseMobileAppClientResult {
  sdkStatus: MobileAppSdkStatus;
  sdkError: string | null;
  client: MobileAppClient | null;
  session: MobileAppSession | null;
  phase: MobileAppSessionPhase;
  queue: MobileAppQueueState | null;
  lastError: string | null;
  embedSrc: string;
  attempt: number;
  retry: () => void;
}

export function useMobileAppClient({
  iframeId,
  embedUrl,
  config,
  enabled,
  onEvent,
  onSessionEnded,
}: UseMobileAppClientOptions): UseMobileAppClientResult {
  const configRef = useRef(config);

  const [attempt, setAttempt] = useState(0);
  const [sdkStatus, setSdkStatus] = useState<MobileAppSdkStatus>('idle');
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [client, setClient] = useState<MobileAppClient | null>(null);
  const [session, setSession] = useState<MobileAppSession | null>(null);
  const [phase, setPhase] = useState<MobileAppSessionPhase>('idle');
  const [queue, setQueue] = useState<MobileAppQueueState | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  const reportEvent = useEffectEvent(
    (source: 'sdk' | 'client' | 'session', label: string, detail?: unknown) => {
      onEvent?.(source, label, detail);
    }
  );

  const reportSessionEnded = useEffectEvent(() => {
    onSessionEnded?.();
  });

  useEffect(() => {
    configRef.current = config;
  });

  useEffect(() => {
    if (!enabled) return undefined;

    type Handler = (payload: unknown) => void;
    let cancelled = false;
    let activeClient: MobileAppClient | null = null;
    let activeSession: MobileAppSession | null = null;
    const clientHandlers: Array<{ event: string; handler: Handler }> = [];
    const sessionHandlers: Array<{ event: string; handler: Handler }> = [];

    reportEvent('sdk', 'Loading Mobile App SDK', { iframeId, attempt });

    loadMobileAppSdk()
      .then(() => {
        if (cancelled) return null;
        setSdkStatus('loading');
        setSdkError(null);
        reportEvent('sdk', 'SDK loaded');
        if (!window.appetize) {
          throw new Error('SDK global missing after load');
        }
        return window.appetize.getClient(`#${iframeId}`, configRef.current);
      })
      .then((maybeClient) => {
        if (cancelled || !maybeClient) return;
        activeClient = maybeClient;
        setClient(maybeClient);
        setSdkStatus('ready');
        reportEvent('client', 'Client ready');

        TRACKED_CLIENT_EVENTS.forEach((eventName) => {
          const handler: Handler = (payload) => {
            reportEvent('client', `client:${eventName}`, payload);

            if (eventName === 'queue') {
              setQueue(parseMobileAppQueuePayload(payload));
              setPhase('queued');
            }
            if (eventName === 'queueEnd') {
              setQueue(null);
            }
            if (eventName === 'sessionRequested') {
              setPhase((prev) => (prev === 'queued' ? prev : 'starting'));
            }
            if (eventName === 'session') {
              const newSession = isMobileAppSession(payload) ? payload : null;
              setSession(newSession);
              activeSession = newSession;
              setPhase('active');
              setQueue(null);
              if (newSession) {
                TRACKED_SESSION_EVENTS.forEach((sessionEventName) => {
                  const sessionHandler: Handler = (sessionPayload) => {
                    reportEvent(
                      'session',
                      `session:${sessionEventName}`,
                      sessionPayload
                    );
                  };
                  newSession.on(sessionEventName, sessionHandler);
                  sessionHandlers.push({
                    event: sessionEventName,
                    handler: sessionHandler,
                  });
                });
              }
            }
            if (eventName === 'sessionEnded') {
              setSession(null);
              activeSession = null;
              setPhase('ended');
              reportSessionEnded();
            }
            if (eventName === 'error') {
              setLastError(readMobileAppErrorMessage(payload));
              setPhase('error');
            }
          };
          maybeClient.on(eventName, handler);
          clientHandlers.push({ event: eventName, handler });
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setSdkStatus('error');
        setSdkError(message);
        reportEvent('sdk', 'SDK load failed', { message });
      });

    return () => {
      cancelled = true;
      if (activeSession) {
        sessionHandlers.forEach(({ event, handler }) => {
          activeSession?.off(event, handler);
        });
      }
      if (activeClient) {
        clientHandlers.forEach(({ event, handler }) => {
          activeClient?.off(event, handler);
        });
        activeClient.endSession().catch(() => undefined);
      }
    };
  }, [enabled, iframeId, attempt]);

  function retry() {
    setSdkStatus('idle');
    setSdkError(null);
    setClient(null);
    setSession(null);
    setPhase('idle');
    setQueue(null);
    setLastError(null);
    setAttempt((prev) => prev + 1);
  }

  const embedSrc = buildMobileAppEmbedUrl(embedUrl, config.language);

  return {
    sdkStatus,
    sdkError,
    client,
    session,
    phase,
    queue,
    lastError,
    embedSrc,
    attempt,
    retry,
  };
}
